import HelpPane from 'components/Common/HelpPane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { fillArray } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import { Fragment } from 'react'
import styled from 'styled-components'
import CarHireImportantInformationSkeleton from './CarHireImportantInformationSkeleton'
import { TabOptions } from './CarHireInclusionsAndDetailsModal.types'
import { useIntl } from 'react-intl'
import CarHireImportantInformationText, { normaliseCarHireImportantInformationParagraph } from './CarHireImportantInformationText'

const ImportantInfoSection = styled(Group)<{ gap: number }>`
  padding: ${({ gap = 0 }) => rem(gap)} 0;
`

// If this is an 'odd' child, it's not actually inbetween anything, don't show it
const DetailsDivider = styled(Divider)`
  &:last-child {
    display: none;
  }
`

const ParagraphList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  list-style: none;
  margin: 0;
  padding: 0;
`

const ParagraphListItem = styled.li`
  display: grid;
  grid-template-columns: ${rem(6)} minmax(0, 1fr);
  column-gap: ${rem(14)};
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(14)};
  line-height: ${rem(18)};

  &::before {
    content: "•";
  }
`

const ParagraphText = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`

interface Props {
  offerImportantInformation?: Array<App.CarHireImportantInformation>
  displayCategory: string
  hasError: boolean
  showTitle?: boolean
}
function CarHireImportantInformationList(props: Props) {
  const { offerImportantInformation, displayCategory, hasError, showTitle = true } = props
  const intl = useIntl()

  const isParagraphAllowed = (paragraph: string): boolean => {
    const allowedStarts = [
      'Theft waiver',
      'Collision damage',
      'Personal accident',
    ]
    return allowedStarts.some(item => paragraph.startsWith(item))
  }

  const errorTitle = intl.formatMessage({
    id: 'L8W6adLw27aL41QheJJ-',
    defaultMessage: 'Oh no, something went wrong',
    description: 'Error heading when car hire important information fails to load',
  })

  const errorBody = intl.formatMessage({
    id: 'RkaJRzG74UDSCe2PRz8D',
    defaultMessage: 'We ran into a problem when trying to load the information. Please go back and try again or call our support team on the number below.',
    description: 'Error message when car hire important information fails to load',
  })

  return (
    <>

      {hasError && (
        <div>
          <VerticalSpacer gap={16}>
            <Heading variant="heading5">{errorTitle}</Heading>
            <BodyText variant="medium">{errorBody}</BodyText>
          </VerticalSpacer>
          <HelpPane />
        </div>
      )}

      {!hasError && offerImportantInformation?.length === 0 && (
        <>
          {fillArray(1).map((_, index) => (
            <CarHireImportantInformationSkeleton key={index} />
          ))}
        </>
      )}

      {offerImportantInformation && offerImportantInformation?.length > 0 && (
        <ImportantInfoSection direction="vertical" gap={showTitle ? 16 : 0}>
          {offerImportantInformation.filter((info) => info.category === displayCategory).map((info, infoIndex) => {
            const visibleParagraphs = info.paragraphs
              .map(normaliseCarHireImportantInformationParagraph)
              .filter((paragraph) => paragraph && (displayCategory !== TabOptions.COVERAGE || isParagraphAllowed(paragraph)))

            if (visibleParagraphs.length === 0) return null

            return (
              <Fragment key={`${info.category}-${info.title}-${infoIndex}`}>
                {showTitle && <BodyText key={info.title} weight="bold" variant="medium">{info.title}</BodyText>}
                <ParagraphList>
                  {visibleParagraphs.map((paragraph, paragraphIndex) => (
                    <ParagraphListItem key={`${info.title}-${paragraphIndex}`}>
                      <ParagraphText><CarHireImportantInformationText text={paragraph} /></ParagraphText>
                    </ParagraphListItem>
                  ))}
                </ParagraphList>
                <DetailsDivider key={info.title} kind="primary" />
              </Fragment>
            )
          })}
        </ImportantInfoSection>
      )}
    </>
  )
}

export default CarHireImportantInformationList
